import './settings.scss';
import domReady from '@wordpress/dom-ready';
import {createRoot, useEffect, useState, render} from '@wordpress/element';
import {__} from '@wordpress/i18n';
import {
    Button,
    Panel,
    PanelBody,
    PanelRow,
    TextControl,
    ToggleControl
} from '@wordpress/components';
import apiFetch from "@wordpress/api-fetch";
import {useDispatch} from "@wordpress/data";
import {store as noticesStore} from '@wordpress/notices';
import {Notices} from "./components/Notices";


/** SETTINGS **/

const useSettings = () => {
    const [cdnDomain, setCdnDomain] = useState();
    const [sdkKey, setSdkKey] = useState();
    const [apiKey, setApiKey] = useState();
    const [sendingEnabled, setSendingEnabled] = useState();
    const [autoSendEnabled, setAutoSendEnabled] = useState();
    const [initializationDisabled, setInitializationDisabled] = useState();
    const [initialInitializationDisabled, setInitialInitializationDisabled] = useState();
    const {
        createSuccessNotice,
        createErrorNotice,
        removeNotice
    } = useDispatch(noticesStore);


    useEffect(() => {
        apiFetch({path: '/wp/v2/settings'}).then((settings) => {
            const pushly = settings?.pushly ?? {};
            setSdkKey(pushly.sdk_key === undefined ? null : pushly.sdk_key);
            setApiKey(pushly.api_key === undefined ? null : pushly.api_key);
            setSendingEnabled(pushly.sending_enabled === undefined ? false : pushly.sending_enabled);
            setAutoSendEnabled(pushly.auto_send_enabled === undefined ? null : pushly.auto_send_enabled);
            setInitializationDisabled(pushly.initialization_disabled === undefined ? false : pushly.initialization_disabled);
            setInitialInitializationDisabled(pushly.initialization_disabled === undefined ? false : pushly.initialization_disabled);
            setCdnDomain(pushly_env.CDN_DOMAIN)
        });
    }, []);

    useEffect(() => {
        if (sendingEnabled && autoSendEnabled === null) {
            setAutoSendEnabled(sendingEnabled);
        }
    }, [sendingEnabled])

    const saveSettings = () => {
        if (sendingEnabled && !apiKey) {
            removeNotice('pushly')
            createErrorNotice('API Key must be provided when sending is enabled.', {
                id: 'pushly',
                type: 'default',
                explicitDismiss: true,
            });
            return;
        }

        // we are going to resolve additional domain settings here to add to the settings store
        fetch(`https://${cdnDomain}/domain-settings/${sdkKey}`).then((response) => {
            if (response.ok) {
                response.json().then((settings) => {
                    // this comes from wp_add_inline_script in class-pushly-admin-settings
                    apiFetch.use(apiFetch.createNonceMiddleware(pushly_env.API_NONCE));
                    apiFetch({
                        path: '/wp/v2/settings',
                        method: 'POST',
                        data: {
                            pushly: {
                                domain_id: settings.domain.id,
                                sdk_key: sdkKey,
                                api_key: apiKey,
                                sending_enabled: sendingEnabled,
                                auto_send_enabled: autoSendEnabled,
                                initialization_disabled: initializationDisabled,
                            },
                        },
                    }).then(() => {
                        removeNotice('pushly')
                        createSuccessNotice(__('Settings saved', 'pushly'), {
                            id: 'pushly',
                            type: 'default'
                        })
                    }).catch((reason) => {
                        // sanitize to user-friendly error message
                        const message = reason.data.details.pushly.message
                            .replace('of pushly', '').replace('pushly[', '').replace(']', '').replace('.', '')
                            .split("_")
                            .map(snake => snake.split("_")
                                .map(substr => substr.charAt(0)
                                        .toUpperCase() +
                                    substr.slice(1))
                                .join(" "))
                            .join(" ").trim();

                        removeNotice('pushly')
                        createErrorNotice(message, {
                            id: 'pushly',
                            type: 'default',
                            explicitDismiss: true
                        });
                    });
                });
            } else {
                removeNotice('pushly')
                createErrorNotice(
                    __('The provided SDK Key is not valid', 'pushly'), {
                        id: 'pushly',
                        type: 'default',
                        explicitDismiss: true,
                    }
                );

            }
        })
    };

    return {
        sdkKey, setSdkKey,
        apiKey, setApiKey,
        sendingEnabled, setSendingEnabled,
        autoSendEnabled, setAutoSendEnabled,
        initializationDisabled, setInitializationDisabled,
        initialInitializationDisabled,
        saveSettings
    };
};

const SettingsPage = () => {
    const {
        sdkKey, setSdkKey,
        apiKey, setApiKey,
        sendingEnabled, setSendingEnabled,
        autoSendEnabled, setAutoSendEnabled,
        initializationDisabled, setInitializationDisabled,
        initialInitializationDisabled,
        saveSettings
    } = useSettings();

    return (
        <>
            <h1>Pushly Settings</h1>
            <Notices/>
            <Panel>
                <PanelBody
                    title={__('Configuration', 'pushly')}
                >
                    <PanelRow>
                        <TextControl
                            name="sdkKey"
                            label={__('SDK Key', 'pushly')}
                            help={__('Enter the SDK Key associated to this Wordpress installation.',
                                'pushly')}
                            value={sdkKey}
                            onChange={(value) => setSdkKey(value)}
                            type="api-key"
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                        />
                    </PanelRow>
                </PanelBody>
                <PanelBody
                    title={__('Notifications', 'pushly')}
                >
                    <PanelRow>
                        <ToggleControl
                            name="sendingEnabled"
                            checked={sendingEnabled}
                            help={__('When enabled, a new section will be added to the post editor that allows ' +
                                'you to send a notification.', 'pushly')}
                            label={__('Send Notifications for New Posts', 'pushly')}
                            onChange={(value) => setSendingEnabled(value === true)}
                            __nextHasNoMarginBottom
                        />
                    </PanelRow>
                    <PanelRow>
                        <ToggleControl
                            name="autoSendEnabled"
                            checked={sendingEnabled && (autoSendEnabled || autoSendEnabled === null)}
                            help={__('When enabled, the checkbox to send a notification on the post editor w' +
                                'ill be checked by default.', 'pushly')}
                            label={__('Automatic Sending', 'pushly')}
                            disabled={!sendingEnabled}
                            onChange={(value) => setAutoSendEnabled(value)}
                            __nextHasNoMarginBottom
                        />
                    </PanelRow>
                    <PanelRow>
                        <TextControl
                            name="apiKey"
                            type="password"
                            label={__('API Key')}
                            help={__('Provide the Wordpress API Key to enable notification sending ' +
                                'directly from the plugin.', 'pushly')}
                            value={apiKey}
                            disabled={!sendingEnabled}
                            onChange={(value) => setApiKey(value)}
                            __next40pxDefaultSize
                            __nextHasNoMarginBottom
                        />
                    </PanelRow>
                </PanelBody>
                <PanelBody
                    title={__('Advanced', 'pushly')}
                    initialOpen={initialInitializationDisabled}
                >
                    <ToggleControl
                        name="initializationDisabled"
                        checked={initializationDisabled}
                        help={__('When this box is checked the Pushly SDK will not automatically run on your ' +
                            'site; Existing subscribers can still receive notifications.', 'pushly')}
                        label={__('Disable SDK Initialization', 'pushly')}
                        onChange={(value) => setInitializationDisabled(value)}
                        __nextHasNoMarginBottom
                    />
                </PanelBody>
            </Panel>
            <Button
                variant="primary"
                onClick={saveSettings}
                __next40pxDefaultSize
            >
                {__('Save', 'pushly')}
            </Button>
        </>
    );
};


domReady(() => {
    // pushly_env from wp_add_inline_script in class-pushly-admin-settings; if it is not set then something is wrong
    if (typeof pushly_env !== 'undefined' && pushly_env?.CDN_DOMAIN && pushly_env?.API_NONCE) {
        const target = document.getElementById('pushly-settings');
        if (target) {
            if (createRoot) {
                createRoot(target).render(<SettingsPage/>);
            } else {
                // createRoot was added in React 18 WP 6.2 but we need to support 6.1 for the first release;
                render(<SettingsPage/>, target);
            }
        }
    }
});